'use client';

import {
  createContext, ReactNode, useCallback, useContext, useMemo, useRef,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';

interface McSharedStateValue {
  /** Bestie chat stream — hoisted to the MC shell so it survives navigation between views. */
  stream: ReturnType<typeof useStreamStateV2>['stream'];
  /** Submit a chat message to the Bestie stream. */
  submitChat: (text: string) => Promise<void>;
  /** Flag that the next chat view should open its input ready to type (e.g. when the
   *  user picks "coaching" from the prompt-bar menu). Lives here because ChatBranch
   *  remounts across the search↔chat mode switch, so a window event would race. */
  requestChatFocus: () => void;
  /** Read-and-clear the chat-focus request. Returns true once per request. */
  consumeChatFocusRequest: () => boolean;
}

const McSharedStateContext = createContext<McSharedStateValue | undefined>(undefined);

/**
 * @description Holds the genuinely-shared live state for Mission Control's candidate shell —
 * currently the Bestie chat stream and its submit handler. Mounted once in the persistent MC
 * layout so the chat survives navigating between candidate views (search ↔ coaching ↔ list),
 * which previously only worked because MC avoided navigation entirely.
 *
 * Reproduces the chat-submit wiring that used to live inside JobListContent.
 */
export const McSharedStateProvider = ({ children, locale }: { children: ReactNode; locale: Locale }) => {
  const { stream, startStream } = useStreamStateV2(null);
  const anonSessionId = useAnonSessionId();

  const streamHeaders = useMemo(() => ({
    'x-anon-session-id': anonSessionId ?? '',
  }), [anonSessionId]);

  const submitChat = useCallback(async (text: string) => {
    const message = { id: uuidv4(), content: text, type: 'human' as const };
    await startStream(message, '', {
      headers: streamHeaders,
      configurations: { user_locale: locale || 'ro' },
    });
  }, [startStream, streamHeaders, locale]);

  // One-shot intent so a chat-view mount can auto-open its input. A ref (not state)
  // because it's consumed by an effect on the next mount, not rendered.
  const chatFocusRequestRef = useRef(false);
  const requestChatFocus = useCallback(() => { chatFocusRequestRef.current = true; }, []);
  const consumeChatFocusRequest = useCallback(() => {
    const requested = chatFocusRequestRef.current;
    chatFocusRequestRef.current = false;
    return requested;
  }, []);

  const value = useMemo(
    () => ({
      stream, submitChat, requestChatFocus, consumeChatFocusRequest,
    }),
    [stream, submitChat, requestChatFocus, consumeChatFocusRequest],
  );

  return <McSharedStateContext.Provider value={value}>{children}</McSharedStateContext.Provider>;
};

/** Access the MC shared chat stream + submit handler. Returns undefined outside the candidate shell. */
export const useMcSharedStateOptional = () => useContext(McSharedStateContext);

export const useMcSharedState = () => {
  const ctx = useContext(McSharedStateContext);
  if (!ctx) throw new Error('useMcSharedState must be used within McSharedStateProvider');
  return ctx;
};
